import { Message, Spinner } from 'Components/Core';
import { GridDefinition } from 'Components/WorksheetGrids';
import FixedWorksheetGridHeader from 'Components/WorksheetGrids/Core/Header/FixedWorksheetGridHeader';
import { getGridDefinition } from 'Components/WorksheetGrids/WorksheetGridDefinitions';
import { actions as appActions } from 'Data/ReduxViewModels/App';
import { TitleSource } from 'Data/ReduxViewModels/App/types';
import { actions } from 'Data/ReduxViewModels/WorksheetGrid';
import { hasEmptyValue } from 'Lib/Any';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import Page from '../Core/Page';
import './WorksheetGridPage.scss';

type WorksheetGridPageProps = {
  gridDefinition: GridDefinition;
  gridType: string;
};

/**
 * Page for an individual worksheet grid.
 */
const WorksheetGridPage: React.FC<WorksheetGridPageProps> = ({ gridDefinition, gridType }) => {
  const {
    worksheetType,
    title,
    shortTitle,
    query,
    entryComponent: EntryComponent,
    controlsComponent: ControlsComponent,
  } = gridDefinition;

  const dispatch = useDispatch();
  const stateGridType = useSelector((state) => state.worksheetGrid.gridType);
  const isPageReady = gridType === stateGridType; // Don't show page until state.worksheetGrid is for the grid we're loading
  const isRefreshing = useSelector((state) => state.worksheetGrid.status.refreshing);
  const isQueryReady = useSelector((state) => state.worksheetGrid.status.queryReady);
  const worksheetsLoaded = useSelector(
    (state) => state.worksheetGrid.worksheets && Object.values(state.worksheetGrid.worksheets).length > 0
  );
  const SpinnerComponent = (
    <div style={{ position: 'relative', height: '100%' }}>
      <Spinner large />
    </div>
  );

  // Initialise state if we have changed grids, otherwise refresh current grid.
  useEffect(() => {
    if (gridType !== stateGridType) dispatch(actions.init({ gridType, worksheetType, query }));
  }, [dispatch, worksheetType, query, gridType, stateGridType]);

  useEffect(() => {
    return () => {
      dispatch(actions.cleanup());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(appActions.setPageTitle({ pageTitle: shortTitle, source: TitleSource.Specific }));
    return () => {
      dispatch(appActions.setPageTitle({ pageTitle: undefined, source: TitleSource.Specific }));
    };
  }, [dispatch, shortTitle, isPageReady]);

  return (
    <Page>
      {isPageReady ? (
        <div className="WorksheetGridPage">
          <FixedWorksheetGridHeader pageIdentifier={title} />
          <div className="WorksheetGridPage-Controls">
            <ControlsComponent />
          </div>
          <div className="WorksheetGridPage-Entry">
            <EntryComponent hidden={isRefreshing || !worksheetsLoaded} />
            {isRefreshing ? (
              SpinnerComponent
            ) : isQueryReady && !worksheetsLoaded ? (
              <div style={{ padding: '20px' }}>
                <Message>No results found.</Message>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div style={{ height: '100vh' }}>{SpinnerComponent}</div>
      )}
    </Page>
  );
};

/**
 * Landing page for an individual worksheet grid ("bulk entry")
 *
 * Takes the key from the route and attempts to find the data for the grid page. Shows an error if key is invalid.
 */
const WorksheetGridLandingPage: React.FC = () => {
  const params = useParams();
  const gridType = params.component!;
  const gridDefinition = getGridDefinition(gridType!);

  if (hasEmptyValue(gridDefinition))
    return (
      <Page>
        <Message severity="error">Bulk entry could not be found for key "{gridType}".</Message>
      </Page>
    );

  return <WorksheetGridPage gridDefinition={gridDefinition} gridType={gridType} />;
};

export default WorksheetGridLandingPage;
